<template>
  <div class="p-users-access-schemes">
    <v-row class="mb-10">
      <v-col md="3">
        <p-search-input
          v-model="search"
          :items="searchSuggestions"
          @submit="searchSchemes"
        ></p-search-input>
      </v-col>
      <template v-if="hasAccess()">
        <v-spacer></v-spacer>
        <v-col class="text-right">
          <v-btn
            class="p-btn-rounded ml-4"
            large
            rounded
            @click="openEditDialog(null)"
          >
            {{ $t('scheme.createScheme') }}
          </v-btn>
        </v-col>
      </template>
    </v-row>

    <div
      v-for="scheme of paginatedSchemes"
      :key="scheme.id"
      class="py-3"
    >
      <scheme
        :scheme="scheme"
        @editScheme="openEditDialog(scheme)"
        @deleteScheme="openDeleteDialog(scheme)"
      ></scheme>
    </div>

    <v-divider class="mt-15"></v-divider>
    <v-pagination
      v-model="page"
      :length="pageCount"
      :total-visible="15"
      class="mt-15"
    ></v-pagination>

    <v-row v-if="noData">
      <v-col>
        <v-divider class="mb-16"></v-divider>
        <p-no-data
          :text="$t('scheme.noSchemes')"
          :btn-text="$t('scheme.createScheme')"
          :hide-btn="!hasAccess()"
          img="scheme.png"
          @btn-click="openEditDialog"
        ></p-no-data>
      </v-col>
    </v-row>

    <scheme-edit-dialog
      :dialog.sync="editDialog"
      :scheme="editScheme"
    ></scheme-edit-dialog>

    <p-delete-dialog
      :dialog.sync="deleteDialog"
      :item="deletedScheme"
      :item-data="deletedSchemeData"
      :load-relations="loadRelations"
      :delete-item="deleteScheme"
      @itemDeleted="onItemDeleted"
    ></p-delete-dialog>
  </div>
</template>


<script>
import { mapActions, mapGetters } from 'vuex'

import PSearchInput from '@/components/PSearchInput'
import PNoData from '@/components/PNoData'
import PDeleteDialog from '@/components/PDeleteDialog'

import Scheme from './Scheme'
import SchemeEditDialog from './SchemeEditDialog'

import snackbarMixin from '@/mixins/snackbar.mixin'
import pageMixin from '@/mixins/page.mixin'
import accessMixin from '@/mixins/access.mixin'


export default {
  components: {
    PSearchInput,
    PNoData,
    PDeleteDialog,
    Scheme,
    SchemeEditDialog
  },


  mixins: [
    snackbarMixin,
    pageMixin,
    accessMixin
  ],


  data () {
    return {
      search: '',
      page: 1,
      itemsPerPage: 5,
      schemesLoaded: false,
      editDialog: false,
      editScheme: null,
      deleteDialog: false,
      deletedScheme: null,
      deletedSchemeData: null
    }
  },


  computed: {
    ...mapGetters('scheme', ['schemes']),
    ...mapGetters('user', ['managers', 'managerById']),

    pageCount () {
      return Math.ceil(this.searchedSchemes.length / this.itemsPerPage)
    },

    paginatedSchemes () {
      return this.searchedSchemes.slice((this.page - 1) * this.itemsPerPage, this.page * this.itemsPerPage)
    },

    searchedSchemes () {
      if (this.search) {
        return this.schemes.filter(scheme => scheme.name.includes(this.search))
      }
      return this.schemes
    },

    noData () {
      return this.schemesLoaded && this.schemes.length === 0
    },

    accessOptions () {
      return [
        { value: true, text: this.$t('scheme.accessOptions.write') },
        { value: false, text: this.$t('scheme.accessOptions.read') }
      ]
    },

    searchSuggestions () {
      return this.schemes.map(scheme => scheme.name)
    }
  },


  methods: {
    ...mapActions('scheme', ['loadSchemes', 'loadSchemeUser', 'updateSchemeUser', 'loadRelations', 'deleteScheme']),
    ...mapActions('user', ['loadManagers']),

    openEditDialog (scheme) {
      this.editScheme = scheme
      this.editDialog = true
    },

    openDeleteDialog (scheme) {
      try {
        if (scheme) {
          this.deletedScheme = scheme
          this.deletedSchemeData = {
            type: this.$t('entity.scheme'),
            title: scheme.name
          }
        }
        this.deleteDialog = true
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    },

    onItemDeleted () {
      this.showSnackbarSuccess(this.$t('scheme.schemeDeleted'))
    },

    async updateAccessClaim (value, scheme, schemeUser) {
      try {
        this.showSnackbarProcessing()
        await this.updateSchemeUser({ schemeId: scheme.id, schemeUser: schemeUser })
        this.showSnackbarSuccess(this.$t('saved'))
      } catch (error) {
        this.showSnackbarError()
        this.reloadSchemeUser(scheme.id, schemeUser.userId)
        this.$log.error(error)
      }
    },

    async reloadSchemeUser (schemeId, userId) {
      try {
        await this.loadSchemeUser({ schemeId, userId })
      } catch (error) {
        this.$log.error(error)
      }
    },

    async searchSchemes (value) {
      await this.reloadSchemes(1, value)
    },

    async reloadSchemes (page, search) {
      try {
        this.startPageLoading()
        this.page = page || this.page
        this.schemesLoaded = false
        await this.loadSchemes({
          skip: (this.page - 1) * this.itemsPerPage,
          take: this.itemsPerPage,
          search: search || this.search
        })
        this.schemesLoaded = true
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      } finally {
        this.stopPageLoading()
      }
    }
  },


  async created () {
    try {
      this.startPageLoading()
      await this.loadManagers()
      await this.loadSchemes()
      this.schemesLoaded = true
    } catch (error) {
      this.showSnackbarError()
      this.$log.error(error)
    } finally {
      this.stopPageLoading()
    }
  }
}
</script>
